import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  useMediaQuery,
  useTheme,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  Collapse,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  fetchSuppliers,
  assignSuppliersToProduct,
  getAllProductsWithSuppliers,
  createSupplier,
} from "../api/adminSupplierApi";
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { styled } from "@mui/material/styles";
import { formatPhoneInput } from "../utils/phone";
interface Supplier {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
}

interface Row {
  product_id: number;
  product_name: string;
  sku: string;
  supplier_id: number | null;
  supplier_name: string | null;
}

const SupplierSelect = styled(Select)(({ theme }) => ({
  '& .MuiSelect-select': {
    padding: theme.spacing(1),
  },
}));

const SupplierManagementPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [saving, setSaving] = useState<{ [productId: number]: boolean }>({});
  const [loading, setLoading] = useState(true);
  const [newSupplier, setNewSupplier] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
  });
  const [showNewSupplierForm, setShowNewSupplierForm] = useState(false);
  const [showAllSuppliers, setShowAllSuppliers] = useState(false);
  
  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [supplierList, productList] = await Promise.all([
          fetchSuppliers(),
          getAllProductsWithSuppliers(),
        ]);
        setSuppliers(supplierList);
        setRows(productList);
      } catch (err) {
        console.error("Failed to load data:", err);
        showSnackbar("Failed to load data", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const showSnackbar = (message: string, severity: "success" | "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleSupplierChange = (product_id: number, newSupplierId: number) => {
    setRows((prev) =>
      prev.map((row) =>
        row.product_id === product_id
          ? {
              ...row,
              supplier_id: newSupplierId,
              supplier_name:
                suppliers.find((s) => s.id === newSupplierId)?.name || null,
            }
          : row
      )
    );
  };

  const handleSave = async (product_id: number, supplier_id: number) => {
    setSaving((prev) => ({ ...prev, [product_id]: true }));
    try {
      await assignSuppliersToProduct({
        product_id,
        supplier_ids: [supplier_id],
        primary_supplier_id: supplier_id,
      });
      showSnackbar("Primary supplier saved successfully!", "success");
    } catch (err) {
      console.error("Failed to assign supplier:", err);
      showSnackbar("Error assigning supplier", "error");
    } finally {
      setSaving((prev) => ({ ...prev, [product_id]: false }));
    }
  };

  const handleCreateSupplier = async () => {
    try {
      const created = await createSupplier(newSupplier);
      setSuppliers((prev) => [
        ...prev,
        { id: created.id, name: newSupplier.name },
      ]);
      setNewSupplier({ name: "", email: "", phone: "", address: "" });
      setShowNewSupplierForm(false);
      showSnackbar("Supplier created successfully!", "success");
    } catch (err) {
      console.error("Failed to create supplier", err);
      showSnackbar("Failed to create supplier", "error");
    }
  };

  const renderDesktopView = () => (
    <Paper sx={{ p: 2, borderRadius: 2, boxShadow: theme.shadows[1] }}>
      <Grid container spacing={1} sx={{ fontWeight: 'bold', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Grid item xs={4}>Product</Grid>
        <Grid item xs={2}>SKU</Grid>
        <Grid item xs={4}>Primary Supplier</Grid>
        <Grid item xs={2} sx={{ textAlign: 'right' }}>Action</Grid>
      </Grid>
      
      {rows.map((row) => (
        <Grid container spacing={1} key={row.product_id} alignItems="center" sx={{ py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Grid item xs={4}>
            <Typography variant="body2" fontWeight="medium">
              {row.product_name}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Chip label={row.sku} size="small" />
          </Grid>
          <Grid item xs={4}>
            <SupplierSelect
              size="small"
              value={row.supplier_id || ""}
              displayEmpty
              onChange={(e) =>
                handleSupplierChange(
                  row.product_id,
                  Number(e.target.value)
                )
              }
              sx={{
                width: '100%',
                backgroundColor: !row.supplier_id ? 'rgba(211,47,47,0.08)' : undefined,
              }}
            >
              <MenuItem value="">
                <em>None Assigned</em>
              </MenuItem>
              {suppliers.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </SupplierSelect>
          </Grid>
          <Grid item xs={2} sx={{ textAlign: 'right' }}>
            <Button
              variant="contained"
              size="small"
              disabled={!row.supplier_id || saving[row.product_id]}
              onClick={() => handleSave(row.product_id, row.supplier_id!)}
              startIcon={saving[row.product_id] ? <CircularProgress size={16} /> : <SaveIcon />}
              sx={{ borderRadius: 2 }}
            >
              {saving[row.product_id] ? '' : 'Save'}
            </Button>
          </Grid>
        </Grid>
      ))}
    </Paper>
  );

  const renderMobileView = () => (
    <Grid container spacing={2}>
      {rows.map((row) => (
        <Grid item xs={12} key={row.product_id}>
          <Card sx={{ borderRadius: 2, boxShadow: theme.shadows[1] }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                {row.product_name}
              </Typography>
              <Chip label={row.sku} size="small" sx={{ mb: 2 }} />
              
              <Divider sx={{ my: 1 }} />

              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Primary Supplier
              </Typography>
              <SupplierSelect
                fullWidth
                size="small"
                value={row.supplier_id || ""}
                displayEmpty
                onChange={(e) =>
                  handleSupplierChange(
                    row.product_id,
                    Number(e.target.value)
                  )
                }
                sx={{
                  mb: 2,
                  backgroundColor: !row.supplier_id ? 'rgba(211,47,47,0.08)' : undefined,
                }}
              >
                <MenuItem value="">
                  <em>None Assigned</em>
                </MenuItem>
                {suppliers.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </SupplierSelect>

              <Button
                fullWidth
                variant="contained"
                size="small"
                disabled={!row.supplier_id || saving[row.product_id]}
                onClick={() => handleSave(row.product_id, row.supplier_id!)}
                startIcon={saving[row.product_id] ? <CircularProgress size={16} /> : <SaveIcon />}
                sx={{ borderRadius: 2 }}
              >
                {saving[row.product_id] ? 'Saving...' : 'Save Supplier'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0,
      }}>
        <Typography variant="h5" fontWeight="bold">Supplier Management</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={() => setShowAllSuppliers(!showAllSuppliers)}
            endIcon={showAllSuppliers ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ borderRadius: 2 }}
          >
            {showAllSuppliers ? 'Hide Suppliers' : 'View All Suppliers'}
          </Button>
          <Button 
            variant="contained" 
            onClick={() => setShowNewSupplierForm(!showNewSupplierForm)}
            startIcon={<AddIcon />}
            sx={{ borderRadius: 2 }}
          >
            New Supplier
          </Button>
        </Box>
      </Box>

      {/* All Suppliers List */}
      {/* All Suppliers List */}
<Collapse in={showAllSuppliers}>
  <Card sx={{ mb: 3, borderRadius: 2, boxShadow: theme.shadows[1] }}>
    <CardContent>
      <Typography variant="h6" gutterBottom>All Suppliers ({suppliers.length})</Typography>
      <List sx={{ maxHeight: 300, overflow: 'auto' }}>
        {suppliers.map((supplier) => (
          <ListItem key={supplier.id} divider>
            <ListItemText 
              primary={supplier.name}
              secondary={
                <>
                  {supplier.email && <div>Email: {supplier.email}</div>}
                  {supplier.phone && <div>Phone: {supplier.phone}</div>}
                  {supplier.address && <div>Address: {supplier.address}</div>}
                </>
              }
              secondaryTypographyProps={{ component: 'div' }}
            />
          </ListItem>
        ))}
        {suppliers.length === 0 && (
          <ListItem>
            <ListItemText primary="No suppliers found" />
          </ListItem>
        )}
      </List>
    </CardContent>
  </Card>
</Collapse>

      {/* New Supplier Form */}
      {showNewSupplierForm && (
        <Card sx={{ mb: 3, p: 2, borderRadius: 2, boxShadow: theme.shadows[1] }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Add New Supplier</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Supplier Name"
                  size="small"
                  value={newSupplier.name}
                  onChange={(e) =>
                    setNewSupplier((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  size="small"
                  value={newSupplier.email}
                  onChange={(e) =>
                    setNewSupplier((prev) => ({ ...prev, email: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  type="tel"
                  inputProps={{ inputMode: "tel" }}
                  size="small"
                  value={newSupplier.phone}
                  onChange={(e) =>
                    setNewSupplier((prev) => ({ ...prev, phone: formatPhoneInput(e.target.value) }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address"
                  size="small"
                  value={newSupplier.address}
                  onChange={(e) =>
                    setNewSupplier((prev) => ({ ...prev, address: e.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowNewSupplierForm(false)}
                  sx={{ borderRadius: 2 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCreateSupplier}
                  disabled={!newSupplier.name.trim()}
                  sx={{ borderRadius: 2 }}
                >
                  Create Supplier
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
          <Typography>No products found.</Typography>
        </Paper>
      ) : isMobile ? renderMobileView() : renderDesktopView()}
    </Box>
  );
};

export default SupplierManagementPage;